import React, { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import {
  useGetGrowthWheelEditorSummariesQuery,
  useGetGrowthWheelEditorStructureQuery,
  useCreateEditorCompetencyAreaMutation,
  useUpdateEditorCompetencyAreaMutation,
  useDeleteEditorCompetencyAreaMutation,
  useReorderEditorCompetenciesOnWheelMutation,
  useCreateEditorCompetencyMutation,
  useUpdateEditorCompetencyMutation,
  useDeleteEditorCompetencyMutation,
  useReorderEditorSubCompetenciesMutation,
} from '../../redux/growth-wheel.endpoints'
import { WheelManagement } from '../../components/GrowthWheelComponents/WheelManagement'
import type {
  CompetencyArea,
  Competency,
} from '../../types/GrowthWheelManagement'
import type { GrowthWheelEditorStructure } from '../../types/GrowthWheel'
import '../../css/growthWheel.scss'

function mapStructureToViewModel(
  data: GrowthWheelEditorStructure,
): { areas: CompetencyArea[]; competencies: Competency[] } {
  const rawAreas = data.competencyAreas ?? []
  const areas: CompetencyArea[] = rawAreas.map(a => ({
    id: String(a.id),
    name: a.name,
    description: a.description,
  }))
  const competencies: Competency[] = rawAreas.flatMap(a =>
    a.competencies.map(c => ({
      id: String(c.id),
      name: c.name,
      area: String(a.id),
      description: c.description,
      levelDescriptions: c.levelDescriptions || {
        1: '',
        2: '',
        3: '',
        4: '',
        5: '',
      },
    })),
  )
  return { areas, competencies }
}

const GrowthWheelEditorDetail = () => {
  const { growthWheelId: growthWheelIdParam } = useParams<{
    growthWheelId: string
  }>()
  const growthWheelId = Number(growthWheelIdParam)
  const skip = !growthWheelIdParam || Number.isNaN(growthWheelId)

  const { data: wheels } = useGetGrowthWheelEditorSummariesQuery()
  const wheelSummary = wheels?.find(w => w.id === growthWheelId)

  const { data: structure, isLoading, isError, refetch } =
    useGetGrowthWheelEditorStructureQuery(
      { growthWheelId },
      { skip },
    )

  const [createArea] = useCreateEditorCompetencyAreaMutation()
  const [updateArea] = useUpdateEditorCompetencyAreaMutation()
  const [deleteArea] = useDeleteEditorCompetencyAreaMutation()
  const [reorderCompetenciesOnWheel] = useReorderEditorCompetenciesOnWheelMutation()
  const [createCompetency] = useCreateEditorCompetencyMutation()
  const [updateCompetency] = useUpdateEditorCompetencyMutation()
  const [deleteCompetency] = useDeleteEditorCompetencyMutation()
  const [reorderSubCompetencies] = useReorderEditorSubCompetenciesMutation()

  const viewModel = useMemo(
    () => (structure ? mapStructureToViewModel(structure) : null),
    [structure],
  )
  const editingRoleName =
    structure?.growthWheel.roleName ?? wheelSummary?.roleName ?? null

  const handleUpdateArea = async (
    areaId: string,
    updates: Partial<CompetencyArea>,
  ) => {
    await updateArea({
      growthWheelId,
      areaId: Number(areaId),
      body: {
        name: updates.name,
        description: updates.description,
      },
    }).unwrap()
  }

  const handleDeleteArea = async (areaId: string) => {
    await deleteArea({
      growthWheelId,
      areaId: Number(areaId),
    }).unwrap()
  }

  const handleAddArea = async (area: Omit<CompetencyArea, 'id'>) => {
    await createArea({
      growthWheelId,
      body: {
        name: area.name,
        description: area.description,
      },
    }).unwrap()
  }

  const handleUpdateCompetency = async (
    competencyId: string,
    updates: Partial<Competency>,
  ) => {
    await updateCompetency({
      growthWheelId,
      competencyId: Number(competencyId),
      body: {
        name: updates.name,
        description: updates.description,
        levelDescriptions: updates.levelDescriptions,
      },
    }).unwrap()
  }

  const handleDeleteCompetency = async (competencyId: string) => {
    await deleteCompetency({
      growthWheelId,
      competencyId: Number(competencyId),
    }).unwrap()
  }

  const handleAddCompetency = async (
    competency: Omit<Competency, 'id'>,
  ) => {
    await createCompetency({
      growthWheelId,
      areaId: Number(competency.area),
      body: {
        name: competency.name,
        description: competency.description,
      },
    }).unwrap()
  }

  const handleReorderAreas = async (ordered: CompetencyArea[]) => {
    await reorderCompetenciesOnWheel({
      growthWheelId,
      orderedCompetencyIds: ordered.map(a => Number(a.id)),
    }).unwrap()
  }

  const handleReorderCompetencies = async (
    areaId: string,
    inAreaOrder: Competency[],
  ) => {
    await reorderSubCompetencies({
      growthWheelId,
      parentCompetencyId: Number(areaId),
      orderedSubCompetencyIds: inAreaOrder.map(c => Number(c.id)),
    }).unwrap()
  }

  return (
    <main className="container-fluid">
      <div className="gw-editor-landing gw-editor-landing--detail">
        <Link
          to="/edit-growth-wheel"
          className="gw-competency-back-button mb-4"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Growth Wheels
        </Link>
        {editingRoleName && (
          <p className="gw-editor-editing-label">
            Editing: <span>{editingRoleName}</span>
          </p>
        )}

        {isLoading && (
          <p className="text-muted">Loading growth wheel…</p>
        )}
        {isError && (
          <div>
            <p className="text-danger">Could not load this growth wheel.</p>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => refetch()}
            >
              Retry
            </button>
          </div>
        )}
        {!skip && !isLoading && !isError && viewModel && (
          <WheelManagement
            areas={viewModel.areas}
            competencies={viewModel.competencies}
            roleName={structure?.growthWheel.roleName ?? wheelSummary?.roleName}
            wheelImageUrl={
              structure?.growthWheel.imageUrl ?? wheelSummary?.imageUrl ?? null
            }
            onUpdateArea={handleUpdateArea}
            onDeleteArea={handleDeleteArea}
            onAddArea={handleAddArea}
            onUpdateCompetency={handleUpdateCompetency}
            onDeleteCompetency={handleDeleteCompetency}
            onAddCompetency={handleAddCompetency}
            onReorderAreas={handleReorderAreas}
            onReorderCompetencies={handleReorderCompetencies}
          />
        )}
      </div>
    </main>
  )
}

export default GrowthWheelEditorDetail
